import { HostAriaLabelMixin, SchmancyElement, SurfaceMixin } from '@schmancy/mixins/index'
import { css, html, LitElement, nothing } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { cursorGlow } from '@schmancy/directives/cursor-glow'
import { magnetic } from '@schmancy/directives/magnetic'
import { ifDefined } from 'lit/directives/if-defined.js'
import { createRef, ref, Ref } from 'lit/directives/ref.js'
import { when } from 'lit/directives/when.js'
import { BehaviorSubject, fromEvent, Subscription } from 'rxjs'
import { distinctUntilChanged, filter, take, takeUntil, tap } from 'rxjs/operators'
import { SPRING_SNAPPY } from '@schmancy/utils/animation'
import { CONTROL_SIZE, type ControlSize } from '@schmancy/theme/control-size'
import { styleMap } from 'lit/directives/style-map.js'
import { reducedMotion$ } from '@schmancy/directives/reduced-motion'

@customElement('schmancy-details')
export default class SchmancyDetails extends HostAriaLabelMixin(SurfaceMixin(SchmancyElement)) {
	static styles = [
		css`
			:host {
				display: block;
				border-radius: var(--schmancy-radius-xl, 0.875rem); /* mirrors --radius-xl (rounded-xl) */
			}

			:host([overlay]) {
				position: relative;
			}

			:host([open]) {
				z-index: 10;
			}

			details {
				background: inherit;
				color: inherit;
				border-radius: inherit;
			}

			summary::-webkit-details-marker {
				display: none;
			}

			summary {
				list-style: none;
				color: inherit;
			}

			/* CSS grid transition drives height, with a coordinated opacity fade —
			   no competing Web Animations API on this content. */

			.content-wrapper {
				display: grid;
				grid-template-rows: 0fr;
				overflow: hidden;
				opacity: 0;
				transition:
					grid-template-rows 400ms cubic-bezier(0.34, 1.2, 0.64, 1),
					opacity 250ms ease;
			}

			/* Spring easing when linear() is supported */
			@supports (animation-timing-function: linear(0, 1)) {
				.content-wrapper {
					transition:
						grid-template-rows 400ms
							linear(
								0,
								0.006,
								0.025 2.8%,
								0.101 6.1%,
								0.539 18.9%,
								0.721 25.3%,
								0.849 31.5%,
								0.937 38.1%,
								0.968 41.8%,
								0.991 45.7%,
								1.006 50%,
								1.015 54.8%,
								1.017 63.3%,
								1
							),
						opacity 250ms ease;
				}
			}

			.content-wrapper[data-open='true'] {
				grid-template-rows: 1fr;
				opacity: 1;
			}

			.content-inner {
				min-height: 0;
				overflow: hidden;
			}

			/* Chrome 131+: when both ::details-content and interpolate-size are
			   supported, the browser handles height animation natively — including
			   animated close via transition-behavior: allow-discrete. The grid
			   wrapper becomes transparent (display: contents). */
			@supports selector(::details-content) and (interpolate-size: allow-keywords) {
				:host {
					interpolate-size: allow-keywords;
				}

				.content-wrapper {
					display: contents;
				}

				details::details-content {
					block-size: 0;
					overflow-y: clip;
					opacity: 0;
					transition:
						block-size 400ms cubic-bezier(0.34, 1.2, 0.64, 1),
						opacity 250ms ease,
						content-visibility 400ms;
					transition-behavior: allow-discrete;
				}

				details[open]::details-content {
					block-size: auto;
					opacity: 1;
				}
			}

			@media (prefers-reduced-motion: reduce) {
				.content-wrapper {
					transition: none;
				}
				details::details-content {
					transition: none;
				}
			}
		`,
	]
	protected static shadowRootOptions = {
		...LitElement.shadowRootOptions,
		mode: 'open' as const,
		delegatesFocus: true,
	}

	@property() summary = ''
	@property({ type: String, reflect: true }) variant: 'standard' | 'outlined' = 'standard'
	@property({ type: String }) size: ControlSize = 'md'

	@property({ type: Boolean, reflect: true })
	get open() {
		return this.open$.value
	}
	set open(value: boolean) {
		if (this.open$.value !== value) {
			this.open$.next(value)
		}
	}

	@property({ attribute: 'indicator-placement', reflect: true })
	indicatorPlacement: 'start' | 'end' = 'end'

	@property({ type: Boolean, attribute: 'hide-indicator' })
	hideIndicator = false

	@property({ type: Number, attribute: 'indicator-rotate' })
	indicatorRotate: number = 90

	@property({ type: Boolean, reflect: true })
	locked = false

	@property({ type: Boolean, reflect: true })
	overlay = false

	@property({ attribute: 'summary-padding' })
	summaryPadding = 'p-3'

	@property({ attribute: 'content-padding' })
	contentPadding = 'p-3'

	private open$ = new BehaviorSubject<boolean>(false)
	private indicatorRef: Ref<HTMLElement> = createRef()
	private contentRef: Ref<HTMLDivElement> = createRef()
	private currentAnimation?: Animation
	/** null until the indicator element has been synced once — see animateIndicator. */
	private indicatorIsOpen: boolean | null = null
	private closing = false
	private closeSub?: Subscription

	/** True when browser handles close animation natively via ::details-content */
	private nativeAnim =
		typeof CSS !== 'undefined' &&
		!!CSS.supports?.('selector(::details-content)') &&
		!!CSS.supports?.('interpolate-size', 'allow-keywords')

	/** Once opened, content stays rendered (even closed) for smooth animations. */
	@state() private hasOpened = false

	constructor() {
		super()
		this.type = 'containerLowest'
	}

	connectedCallback() {
		super.connectedCallback()

		this.open$
			.pipe(
				distinctUntilChanged(),
				tap(isOpen => {
					if (isOpen && !this.hasOpened) {
						this.hasOpened = true
					}
					this.animateIndicator(isOpen)
					this.updateIndicatorSlot()
				}),
				takeUntil(this.disconnecting),
			)
			.subscribe(() => this.requestUpdate())
	}

	disconnectedCallback() {
		super.disconnectedCallback()
		this.closeSub?.unsubscribe()
	}

	/**
	 * The open$ tap fires before the shadow root has ever rendered, so on a
	 * `<schmancy-details open>` the indicator it wants to turn does not exist
	 * yet. Every render re-offers the sync; the memo makes all but the one that
	 * finds the element a no-op.
	 */
	updated() {
		this.animateIndicator(this.open$.value)
	}

	render() {
		const isOpen = this.open$.value

		const detailsClasses = this.classMap({
			'w-full transition-shadow duration-200 ease-out': true,
			'ring-1 ring-inset ring-control-border': this.variant === 'outlined',
			'overflow-visible': !this.overlay,
			'overflow-visible relative': this.overlay,
		})

		const summaryClasses = this.classMap({
			[this.summaryPadding]: this.variant !== 'outlined',
			'select-none relative flex items-center gap-2 rounded-xl': true,
			'transition-colors duration-150': true,
			'hover:bg-surface-on/5 active:bg-surface-on/8': !this.locked,
			'focus-visible:ring-2 focus-visible:ring-primary-default/50 focus-visible:ring-offset-1': !this.locked,
			'cursor-pointer group': !this.locked,
			'cursor-default': this.locked,
			// Always source order. `indicator-placement` moves the INDICATOR, and
			// a reversed row moved everything — landing the `actions` slot at the
			// visual START of the summary bar in the default 'end' placement, so
			// a row authored as [heading][action][chevron] rendered as
			// [action][heading][chevron]. The indicator is placed by position
			// below instead, which is the only thing the property ever named.
			'flex-row': true,
		})

		const contentClasses = this.classMap({
			[this.contentPadding]: true,
			'text-sm': true,
			'absolute inset-x-0 bg-surface-lowest/55 backdrop-blur-[var(--schmancy-sys-glass-blur)] backdrop-saturate-[var(--schmancy-sys-glass-saturate)] border border-[var(--schmancy-sys-glass-border)] shadow-2xl rounded-b-xl z-20':
				this.overlay,
		})

		const indicator = this.hideIndicator
			? nothing
			: html`
					<span
						${ref(this.indicatorRef)}
						class="flex items-center justify-center w-5 h-5 rounded-full shrink-0 opacity-70 group-hover:opacity-100"
					>
						<slot name="indicator" @slotchange=${this.handleIndicatorSlotChange}>
							<svg width="20" height="20" viewBox="0 0 24 24" fill="none" class="w-5 h-5" aria-hidden="true">
								<path
									d="M9 6L15 12L9 18"
									stroke="currentColor"
									stroke-width="2"
									stroke-linecap="round"
									stroke-linejoin="round"
								/>
							</svg>
						</slot>
					</span>
				`

		return html`
			<details ?open=${isOpen} @toggle=${this.handleToggle} class=${detailsClasses}>
				<summary
					${this.locked ? '' : magnetic({ strength: 2, radius: 50 })}
					${this.locked ? '' : cursorGlow({ radius: 250, intensity: 0.08 })}
					class=${summaryClasses}
					style=${
						this.variant === 'outlined'
							? styleMap({
									minHeight: `${CONTROL_SIZE[this.size].height}px`,
									fontSize: `${CONTROL_SIZE[this.size].labelFont}px`,
									lineHeight: `${CONTROL_SIZE[this.size].labelLeading}px`,
									paddingInline: `${CONTROL_SIZE[this.size].pad.input}px`,
									paddingBlock: `${CONTROL_SIZE[this.size].gap}px`,
									boxSizing: 'border-box',
								})
							: nothing
					}
					tabindex=${this.locked ? -1 : 0}
					role=${ifDefined(this.locked ? undefined : 'button')}
					aria-expanded=${isOpen ? 'true' : 'false'}
					aria-controls="content"
					aria-label=${ifDefined(this.ariaLabel || undefined)}
					@click=${this.handleSummaryClick}
				>
					${when(this.indicatorPlacement === 'start', () => indicator)}

					<span class="flex-1 min-w-0">
						<slot name="summary">${this.summary}</slot>
					</span>

					<slot name="actions"></slot>

					${when(this.indicatorPlacement === 'end', () => indicator)}
				</summary>

				<div
					${ref(this.contentRef)}
					id="content"
					class="content-wrapper"
					data-open=${isOpen && !this.closing}
					aria-hidden=${isOpen ? 'false' : 'true'}
				>
					<div class="content-inner">
						${
							this.hasOpened
								? html`
										<div class=${contentClasses}>
											<slot></slot>
											<slot name="details"></slot>
										</div>
									`
								: nothing
						}
					</div>
				</div>
			</details>
		`
	}

	private handleSummaryClick(e: MouseEvent) {
		if ((e.target as HTMLElement).closest('[slot="actions"]')) {
			e.preventDefault()
			return
		}
		if (this.locked) {
			e.preventDefault()
			return
		}
		if (this.closing) {
			e.preventDefault()
			return
		}

		// Native animated path (Chromium 131+): browser handles everything via CSS
		if (this.nativeAnim) return

		// Fallback: manually animate close via CSS transition, then close native details
		if (this.open$.value) {
			e.preventDefault()
			this.startClose()
		}
	}

	private handleToggle(e: Event) {
		e.stopPropagation()

		const ownDetails = this.shadowRoot?.querySelector('details')
		if (e.target !== ownDetails) return

		const newState = ownDetails.open

		if (this.nativeAnim) {
			// Native path: handle both open and close
			if (this.open$.value !== newState) {
				this.open = newState
				this.dispatchScopedEvent('toggle', { open: newState })
			}
		} else {
			// Fallback path only handles OPEN — close is driven by startClose().
			if (newState && this.open$.value !== newState) {
				this.open = newState
				this.dispatchScopedEvent('toggle', { open: newState })
			}
		}
	}

	/**
	 * Fallback close: trigger CSS grid+opacity transition, then close native <details>.
	 * The CSS transition (400ms) handles the visual collapse — no WAAPI needed.
	 */
	private startClose() {
		this.closing = true
		this.closeSub?.unsubscribe()

		this.contentRef.value?.setAttribute('data-open', 'false')
		this.animateIndicator(false)

		const wrapper = this.contentRef.value
		if (!wrapper) return

		this.closeSub = fromEvent<TransitionEvent>(wrapper, 'transitionend')
			.pipe(
				filter(e => e.propertyName === 'grid-template-rows'),
				take(1),
				tap(() => {
					this.closing = false
					this.open = false
					this.dispatchScopedEvent('toggle', { open: false })
				}),
				takeUntil(this.disconnecting),
			)
			.subscribe()
	}

	private handleIndicatorSlotChange(e: Event) {
		const slot = e.target as HTMLSlotElement
		slot.assignedElements().forEach(el => {
			el.setAttribute('data-open', String(this.open$.value))
		})
	}

	private updateIndicatorSlot() {
		const slot = this.shadowRoot?.querySelector('slot[name="indicator"]') as HTMLSlotElement | null
		if (slot) {
			slot.assignedElements().forEach(el => {
				el.setAttribute('data-open', String(this.open$.value))
			})
		}
	}

	/**
	 * The memo records the angle the indicator is WEARING, so it is written only
	 * once there is an element wearing it — recorded ahead of that, it says the
	 * turn happened while nothing turned, and no later emission of the same
	 * state can correct it. The first sync sets the angle outright instead of
	 * animating: a disclosure that mounts open was never opened by anyone.
	 */
	private animateIndicator(isOpen: boolean) {
		if (this.indicatorIsOpen === isOpen) return

		const indicator = this.indicatorRef.value
		if (!indicator) return

		const firstSync = this.indicatorIsOpen === null
		this.indicatorIsOpen = isOpen
		this.currentAnimation?.cancel()

		if (firstSync || reducedMotion$.value) {
			indicator.style.transform = `rotate(${isOpen ? this.indicatorRotate : 0}deg)`
			return
		}

		this.currentAnimation = indicator.animate(
			[
				{ transform: `rotate(${isOpen ? '0deg' : `${this.indicatorRotate}deg`})` },
				{ transform: `rotate(${isOpen ? `${this.indicatorRotate}deg` : '0deg'})` },
			],
			{
				duration: SPRING_SNAPPY.duration,
				easing: SPRING_SNAPPY.easingFallback,
				fill: 'forwards',
			},
		)
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-details': SchmancyDetails
	}
}
